import { useCallback, useId, useRef, useState } from 'react';
import { Controller, useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Form, Input, Label, Modal, Select, TextArea } from '@shared/components';
import { useAddAgreementNoteMutation } from '../../../hooks/useAddAgreementNoteMutation';
import {
    agreementNoteSchema,
    NOTE_BODY_MAX_LENGTH,
    type AgreementNoteFormInput,
    type AgreementNoteFormValues,
} from '../../../schemas/agreementNote.schema';
import { NOTE_TYPES } from '../../../types/agreementTimeline.types';
import { NOTE_TYPE_LABEL } from '../../../utils/timelineEntryLabels';
import { mapNoteSubmitError } from '../../../utils/mapNoteSubmitError';

interface Props {
    agreementId: string;
    isOpen: boolean;
    onClose: () => void;
}

const NOTE_TYPE_OPTIONS = NOTE_TYPES.map((value) => ({ value, label: NOTE_TYPE_LABEL[value] }));

const EMPTY_FORM: DefaultValues<AgreementNoteFormInput> = { body: '', crmCaseId: '' };

export function AddNoteModal({ agreementId, isOpen, onClose }: Props) {
    const headingId = useId();
    const ids = { noteType: `${headingId}-note-type`, body: `${headingId}-body`, crmCaseId: `${headingId}-crm-case-id` };
    const noteTypeWrapperRef = useRef<HTMLSpanElement>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const mutation = useAddAgreementNoteMutation(agreementId);

    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<AgreementNoteFormInput, unknown, AgreementNoteFormValues>({
        resolver: zodResolver(agreementNoteSchema),
        mode: 'onBlur',
        defaultValues: EMPTY_FORM,
    });

    const bodyErrorId = errors.body ? `${ids.body}-error` : undefined;
    // The shared TextArea exposes no ARIA error props, so the attributes are set on its textarea here.
    const bodyWrapperRef = useCallback(
        (wrapper: HTMLDivElement | null) => {
            const textarea = wrapper?.querySelector('textarea');
            if (!textarea) return;
            if (bodyErrorId) {
                textarea.setAttribute('aria-invalid', 'true');
                textarea.setAttribute('aria-describedby', bodyErrorId);
            } else {
                textarea.removeAttribute('aria-invalid');
                textarea.removeAttribute('aria-describedby');
            }
        },
        [bodyErrorId],
    );

    const handleClose = () => {
        if (mutation.isPending) return;
        setFormError(null);
        clearErrors();
        onClose();
    };

    const handleValid = (values: AgreementNoteFormValues) => {
        setFormError(null);
        mutation.mutate(values, {
            onSuccess: () => {
                reset(EMPTY_FORM);
                onClose();
            },
            onError: (error: unknown) => {
                const { fieldErrors, message } = mapNoteSubmitError(error);
                fieldErrors.forEach(({ field, message: fieldMessage }) => setError(field, { message: fieldMessage }));
                setFormError(message);
            },
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!mutation.isPending}
            ariaLabelledBy={headingId}
            className="max-w-lg p-6"
            initialFocusRef={noteTypeWrapperRef}
        >
            <h2 id={headingId} className="mb-4 text-lg font-semibold">
                Add note
            </h2>
            <Form onSubmit={handleSubmit(handleValid)} noValidate className="space-y-4">
                {formError && <Alert variant="error">{formError}</Alert>}
                <div>
                    <Label htmlFor={ids.noteType}>Note type</Label>
                    <Controller
                        name="noteType"
                        control={control}
                        render={({ field }) => (
                            <span ref={noteTypeWrapperRef}>
                                <Select
                                    id={ids.noteType}
                                    ariaLabel="Note type"
                                    placeholder="Choose a note type"
                                    options={NOTE_TYPE_OPTIONS}
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.noteType}
                                    ariaDescribedBy={errors.noteType ? `${ids.noteType}-error` : undefined}
                                    disabled={mutation.isPending}
                                />
                            </span>
                        )}
                    />
                    <FieldError id={`${ids.noteType}-error`} message={errors.noteType?.message} />
                </div>

                <div>
                    <Label htmlFor={ids.body}>Note</Label>
                    <Controller
                        name="body"
                        control={control}
                        render={({ field }) => (
                            <div ref={bodyWrapperRef} onBlur={field.onBlur}>
                                <TextArea
                                    id={ids.body}
                                    placeholder="What happened?"
                                    rows={6}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={!!errors.body}
                                    disabled={mutation.isPending}
                                />
                                <p className="mt-1 text-right text-xs text-mp-muted" aria-live="polite">
                                    {field.value.length} / {NOTE_BODY_MAX_LENGTH}
                                </p>
                            </div>
                        )}
                    />
                    <FieldError id={`${ids.body}-error`} message={errors.body?.message} />
                </div>

                <div>
                    <Label htmlFor={ids.crmCaseId}>CRM case ID (required for case actions)</Label>
                    <Controller
                        name="crmCaseId"
                        control={control}
                        render={({ field }) => (
                            <Input
                                id={ids.crmCaseId}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={!!errors.crmCaseId}
                                aria-invalid={!!errors.crmCaseId}
                                aria-describedby={errors.crmCaseId ? `${ids.crmCaseId}-error` : undefined}
                                disabled={mutation.isPending}
                            />
                        )}
                    />
                    <FieldError id={`${ids.crmCaseId}-error`} message={errors.crmCaseId?.message} />
                </div>

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="clear" disabled={mutation.isPending} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={mutation.isPending}>
                        {mutation.isPending ? 'Saving…' : 'Save note'}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

function FieldError({ id, message }: { id: string; message?: string }) {
    if (!message) return null;

    return (
        <p id={id} role="alert" className="mt-1.5 text-xs text-mp-error">
            {message}
        </p>
    );
}
